import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import type { ResolvedShortcut } from "@/lib/dashboard-home/resolve-shortcuts";
import { cn } from "@/lib/utils";
import { GlassBlockHeader } from "../GlassBlockHeader";
import { ShortcutsIcon } from "../HomeIcons";
import { GLASS_BLOCK_CLASS, type GlassTheme } from "../glass";
import { ShortcutPill } from "./ShortcutPill";
import { SHORTCUT_GRID_CLASS, shortcutGridStyle } from "./shortcut-grid";

type ShortcutsBlockProps = {
  theme: GlassTheme;
  shortcuts: ResolvedShortcut[];
  onOpen: (shortcut: ResolvedShortcut) => void;
  onManage: () => void;
};

export const ShortcutsBlock = ({
  theme,
  shortcuts,
  onOpen,
  onManage,
}: ShortcutsBlockProps) => {
  const vt = useVisibleT();

  return (
    <section
      className={cn(GLASS_BLOCK_CLASS, "flex flex-col gap-4 p-4")}
      style={theme.surface}
    >
      <GlassBlockHeader
        theme={theme}
        icon={<ShortcutsIcon />}
        title={vt("bo.home.shortcuts.title", "Raccourcis")}
      />

      {shortcuts.length > 0 ? (
        <ul
          className={cn(SHORTCUT_GRID_CLASS, "gap-2.5 md:gap-x-12")}
          style={shortcutGridStyle(shortcuts.length)}
        >
          {shortcuts.map((shortcut) => (
            <li key={shortcut.id} className="min-w-0">
              <button
                type="button"
                onClick={() => onOpen(shortcut)}
                className="block w-full rounded-[1.25rem] transition-transform hover:-translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <ShortcutPill icon={shortcut.icon} label={shortcut.label} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm">
          {vt(
            "bo.home.shortcuts.empty",
            "Aucun raccourci pour l’instant : ajoutez vos actions les plus fréquentes.",
          )}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onManage}
          className="rounded-full border border-solid border-white px-3 py-1.5 text-xs font-semibold backdrop-blur-[25px] transition-opacity hover:opacity-80"
          style={{ color: theme.text }}
        >
          {vt("bo.home.shortcuts.manage", "Gérer les raccourcis")}
        </button>
      </div>
    </section>
  );
};
